import { partnerCockpitCsrfResponseSchema } from '@houzy/schemas/partner-cockpit/user-management';
import { z } from 'zod';

import { PARTNER_COCKPIT_API_PATH_PREFIX, partnerCockpitApiPath } from './config';

const API_BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';

// Mirrors `ErrorCode.InvalidCsrfToken` on the backend. Retrying on a bare 403 would also re-send requests the
// server rejected on their merits (an impersonated session attempting a write, say).
const INVALID_CSRF_TOKEN_ERROR_CODE = 'INVALID_CSRF_TOKEN';

let partnerCockpitCsrfTokenCache: string | null = null;
let partnerCockpitCsrfTokenRequest: Promise<string> | null = null;
let partnerCockpitCsrfGeneration = 0;

// Deliberately leaves an in-flight fetch alone: two requests that fail together both invalidate before
// refetching, and dropping the shared fetch here would put them back to minting a token each. The generation
// counter is what keeps that safe — see `requestPartnerCockpitCsrfToken`.
export function invalidatePartnerCockpitCsrfToken(): void {
    partnerCockpitCsrfGeneration += 1;
    partnerCockpitCsrfTokenCache = null;
}

export class ApiError extends Error {
    status: number;
    errorCode?: string;

    constructor(message: string, status: number, errorCode?: string) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.errorCode = errorCode;
    }
}

export class ApiContractError extends Error {
    readonly path: string;
    readonly issues: z.ZodError['issues'];

    constructor(path: string, error: z.ZodError) {
        super(`Response from ${path} violates the shared schema:\n${z.prettifyError(error)}`);
        this.name = 'ApiContractError';
        this.path = path;
        this.issues = error.issues;
    }
}

// Maps known backend error codes to translation keys. The backend's `error.message` is intended for
// developers (e.g. "Cockpit OTP not valid (purpose=EmailVerification)") and should never be surfaced
// to end users directly. Callers translate the returned key via `useTranslation`.
const ERROR_CODE_TO_MESSAGE_KEY: Record<string, string> = {
    ONETIMEPASSWORD_NOT_VALID: 'ERRORS.AUTH_LINK_INVALID',
    TOO_MANY_REQUESTS: 'ERRORS.TOO_MANY_REQUESTS',
    IMPERSONATION_READ_ONLY: 'ERRORS.IMPERSONATION_READ_ONLY',
    TEAM_MEMBER_ALREADY_EXISTS: 'SETTINGS.MEMBERS.ERRORS.ALREADY_MEMBER',
    CANNOT_REMOVE_SELF_FROM_TEAM: 'SETTINGS.MEMBERS.ERRORS.CANNOT_REMOVE_SELF',
    CANNOT_REMOVE_LAST_TEAM_MEMBER: 'SETTINGS.MEMBERS.ERRORS.CANNOT_REMOVE_LAST',
    TEAM_MEMBER_LIMIT_REACHED: 'SETTINGS.MEMBERS.ERRORS.LIMIT_REACHED',
    COCKPIT_USER_BELONGS_TO_ANOTHER_TEAM: 'SETTINGS.MEMBERS.ERRORS.BELONGS_TO_ANOTHER_TEAM',
    USER_TOS_NOT_ACCEPTED: 'ERRORS.TOS_NOT_ACCEPTED',
    NO_PARTNER_MEMBERSHIP_OR_ONBOARDING_APPLICATION: 'AUTH.LOGIN.NO_COCKPIT_ACCESS',
    ADDRESS_GEOCODE_NOT_FOUND: 'SETTINGS.COMPANY.PROFILE.ERRORS.ADDRESS_NOT_FOUND',
    ONBOARDING_COCKPIT_ACCOUNT_ALREADY_EXISTS: 'ONBOARDING.REGISTER.ERRORS.ACCOUNT_EXISTS_TITLE',
    ONBOARDING_PARTNER_ALREADY_HAS_COCKPIT_ACCOUNT: 'ONBOARDING.REGISTER.ERRORS.PARTNER_CLAIMED_TITLE',
    ONBOARDING_COCKPIT_ACCOUNT_UNAVAILABLE: 'ONBOARDING.REGISTER.ERRORS.ACCOUNT_UNAVAILABLE_TITLE',
};

export function apiErrorMessageKey(error: unknown, fallbackKey: string): string {
    if (!(error instanceof ApiError) || error.errorCode === undefined) {
        return fallbackKey;
    }
    return ERROR_CODE_TO_MESSAGE_KEY[error.errorCode] ?? fallbackKey;
}

// Re-auth flows (change password / change email): the backend reuses WRONG_EMAIL_OR_PASSWORD for both a bad
// password (401) and a taken email (409). Login must not use this and it stays a single vague message so a
// failed attempt can't reveal whether an account exists.
export function credentialErrorMessageKey(error: unknown, fallbackKey: string): string {
    if (error instanceof ApiError && error.errorCode === 'WRONG_EMAIL_OR_PASSWORD') {
        return error.status === 409 ? 'ERRORS.EMAIL_ALREADY_IN_USE' : 'ERRORS.WRONG_CURRENT_PASSWORD';
    }
    return apiErrorMessageKey(error, fallbackKey);
}

export function apiErrorCode(error: unknown): string | undefined {
    return error instanceof ApiError ? error.errorCode : undefined;
}

export function retryQueryUnlessClientError(failureCount: number, error: Error): boolean {
    if (error instanceof ApiContractError) {
        return false;
    }
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
    }
    return failureCount < 2;
}

type ApiRequestOptions = {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: unknown;
    signal?: AbortSignal;
};

// Each `GET /auth/csrf` mints a new token and overwrites the cookie, so concurrent fetches leave the last
// response's cookie paired with some other caller's header. Callers share one round trip instead. The abort
// signal is not forwarded: one caller giving up must not reject the token every other caller is waiting on.
async function fetchPartnerCockpitCsrfToken(): Promise<string> {
    partnerCockpitCsrfTokenRequest ??= requestPartnerCockpitCsrfToken().finally(() => {
        partnerCockpitCsrfTokenRequest = null;
    });
    return partnerCockpitCsrfTokenRequest;
}

async function requestPartnerCockpitCsrfToken(): Promise<string> {
    const generation = partnerCockpitCsrfGeneration;
    const response = await fetch(`${API_BASE_URL}${partnerCockpitApiPath('auth/csrf')}`, {
        credentials: 'include',
    });

    const payload = (await response.json().catch(() => null)) as unknown;

    if (!response.ok) {
        const message = (payload as { message?: string } | null)?.message;
        throw new ApiError(message ?? response.statusText, response.status);
    }

    const parsed = partnerCockpitCsrfResponseSchema.safeParse(payload);
    if (!parsed.success) {
        throw new ApiError('Missing CSRF token in response', response.status);
    }

    // An invalidation while this was in flight means the token is bound to a session identifier we have since
    // left behind. Whoever is awaiting it still gets it and recovers through the 403 retry, but caching it would
    // hand the same doomed token to every later caller too.
    if (generation === partnerCockpitCsrfGeneration) {
        partnerCockpitCsrfTokenCache = parsed.data.csrfToken;
    }
    return parsed.data.csrfToken;
}

async function partnerCockpitCsrfHeaderIfNeeded(
    path: string,
    method: ApiRequestOptions['method'] | undefined,
): Promise<Record<string, string> | undefined> {
    const effectiveMethod = method ?? 'GET';
    if (effectiveMethod === 'GET' || !path.startsWith(PARTNER_COCKPIT_API_PATH_PREFIX)) {
        return undefined;
    }

    const token = partnerCockpitCsrfTokenCache ?? (await fetchPartnerCockpitCsrfToken());

    return {
        'x-csrf-token': token,
    };
}

type ResponseBody = { payload: unknown; isEmpty: boolean };

// The retry decision needs the error code, and a body can only be read once, so every caller shares this read.
async function readResponseBody(response: Response): Promise<ResponseBody> {
    if (response.status === 204 || response.headers.get('content-length') === '0') {
        return { payload: undefined, isEmpty: true };
    }
    return { payload: (await response.json().catch(() => null)) as unknown, isEmpty: false };
}

function errorCodeOf(payload: unknown): string | undefined {
    return (payload as { errorCode?: string } | null)?.errorCode;
}

function parseResponse(path: string, schema: z.ZodType | undefined, payload: unknown): unknown {
    if (!schema) {
        return undefined;
    }
    const normalizedPayload = payload === undefined && schema.safeParse(null).success ? null : payload;
    const result = schema.safeParse(normalizedPayload);
    if (!result.success) {
        throw new ApiContractError(path, result.error);
    }
    return result.data;
}

export async function apiRequest<S extends z.ZodType>(
    path: string,
    options: ApiRequestOptions & { responseSchema: S },
): Promise<z.output<S>>;
export async function apiRequest(path: string, options?: ApiRequestOptions): Promise<void>;
export async function apiRequest(
    path: string,
    options: ApiRequestOptions & { responseSchema?: z.ZodType } = {},
): Promise<unknown> {
    const method = options.method ?? 'GET';

    async function dispatch(): Promise<Response> {
        const csrfHeaders = await partnerCockpitCsrfHeaderIfNeeded(path, method);
        const headers: Record<string, string> = csrfHeaders ? { ...csrfHeaders } : {};
        if (options.body !== undefined && options.body !== null && !(options.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }

        return await fetch(`${API_BASE_URL}${path}`, {
            method,
            credentials: 'include',
            headers: Object.keys(headers).length > 0 ? headers : undefined,
            body:
                options.body instanceof FormData
                    ? options.body
                    : options.body
                      ? JSON.stringify(options.body)
                      : undefined,
            signal: options.signal,
        });
    }

    let response = await dispatch();
    let body = await readResponseBody(response);

    if (
        response.status === 403 &&
        method !== 'GET' &&
        path.startsWith(PARTNER_COCKPIT_API_PATH_PREFIX) &&
        errorCodeOf(body.payload) === INVALID_CSRF_TOKEN_ERROR_CODE
    ) {
        invalidatePartnerCockpitCsrfToken();
        response = await dispatch();
        body = await readResponseBody(response);
    }

    if (body.isEmpty) {
        if (response.ok) {
            return parseResponse(path, options.responseSchema, undefined);
        }
        throw new ApiError(response.statusText, response.status);
    }

    if (!response.ok) {
        const errorPayload = body.payload as { message?: string } | null;
        throw new ApiError(errorPayload?.message ?? response.statusText, response.status, errorCodeOf(body.payload));
    }

    return parseResponse(path, options.responseSchema, body.payload);
}
